'use client';
import React from 'react';

import { ErrorBoundary, Icon } from '@benzinga/core-ui';
import styles from './HeaderDesktop.module.scss';
import { cryptoTickers } from '@benzinga/crypto-manager';
import { BreakingNews } from '@benzinga/basic-news-manager';
import { useEffectDidMount, useHydrate } from '@benzinga/hooks';
import { useIsUserLoggedIn, useUser } from '@benzinga/user-context';

import { faEllipsisH } from '@fortawesome/pro-regular-svg-icons/faEllipsisH';

import { TopBar } from '../TopBar';
import { QuoteBar } from './QuoteBar';
import { convertToRelativeUrl, featuredMenu } from './_mockMenu';
import { BreakingNewsBanner } from '../BreakingNewsBanner';
import { EmailVerificationBanner } from '../EmailVerificationBanner';
import { useRevealOnFirstScroll } from '../../hooks/useRevealOnFirstScroll';
import { useSyncStickyNavOffset } from '../../hooks/useSyncStickyNavOffset';
import { estimateDesktopStickyNavOffsetPx } from '../../utils/estimateDesktopStickyNavOffset';
import { appEnvironment, appName } from '@benzinga/utils';
import { faBars } from '@fortawesome/pro-light-svg-icons/faBars';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { NavigationHeaderProps } from '../../entities';
import { MenuInterface } from './interfaces';
import { NavigationLogo } from './NavigationLogo';
import { Menu, MenuItemLink, MenuItems, MoreMenuItemsDropdown } from './Menu';
import classNames from 'classnames';
import { OptimizelyTopBanner } from './RotatingBannerFallback';
import { AdHeaderBanner } from './AdHeaderBanner';

export { DefaultQuotesList } from './_mockMenu';

export interface NavigationMenus {
  primary: MenuInterface[];
  secondary: MenuInterface[];
}

export interface HeaderDesktopProps extends NavigationHeaderProps {
  breakingNews?: BreakingNews[];
  isActiveHeader?: boolean;
}

export const HeaderDesktop = ({
  breakingNews,
  expandedHeaderDisplayAd,
  hideMenuBar,
  hideQuoteBar,
  hideSearchBar,
  hideTopBar,
  initialTrendingTickers,
  initialTrendingTopics,
  isActiveHeader,
  logoVariant,
  marketTickers,
  menus,
  moneyBannerLink,
  moneyBannerText,
  onSearch,
  primaryQuotesLabel,
  quotes,
  recentTickers,
  shouldRenderHeaderDisplayAd,
  showHeaderDisplayAd,
  showRotatingBanner,
  sticky,
}: HeaderDesktopProps) => {
  const [isMoreItemsDropdownVisible, setIsMoreItemsDropdownVisible] = React.useState(false);
  const desktopMenuWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [breakingNewsVisible, setBreakingNewsVisible] = React.useState(false);
  const estimatedStickyNavOffsetPx = estimateDesktopStickyNavOffsetPx({
    expandedHeaderDisplayAd,
    hideQuoteBar,
    hideTopBar,
    showHeaderDisplayAd,
    showRotatingBanner,
  });
  const estimatedChromePx = estimateDesktopStickyNavOffsetPx({
    expandedHeaderDisplayAd,
    hideQuoteBar,
    hideTopBar,
    showHeaderDisplayAd: false,
    showRotatingBanner,
  });
  const headerAdFallbackPx = estimatedStickyNavOffsetPx - estimatedChromePx;
  const stickyNavHeight = useSyncStickyNavOffset(desktopMenuWrapperRef, Boolean(sticky), estimatedStickyNavOffsetPx);

  const user = useUser();
  const showEmailVerificationBanner = Boolean(user?.email && user.emailVerified === false);
  const emailStripVisible = useRevealOnFirstScroll(showEmailVerificationBanner);

  React.useEffect(() => {
    if (!breakingNews?.length) {
      setBreakingNewsVisible(false);
    }
  }, [breakingNews?.length]);

  React.useEffect(() => {
    if (window?.scrollY > 0) {
      if (!breakingNewsVisible && breakingNews) {
        setBreakingNewsVisible(true);
      }
    }
  }, [breakingNews, breakingNewsVisible]);

  useEffectDidMount(() => {
    window?.addEventListener(
      'scroll',
      () => {
        if (!breakingNewsVisible && breakingNews) {
          setBreakingNewsVisible(true);
        }
      },
      { once: true },
    );
  });

  const tickers = logoVariant === 'crypto' ? cryptoTickers : marketTickers;

  const handleToggleMenuItemsDropdown = () => {
    setIsMoreItemsDropdownVisible(!isMoreItemsDropdownVisible);
  };

  const isMoneyApp = appEnvironment().isApp(appName.money);

  return (
    <>
      {sticky ? (
        <div
          aria-hidden
          className={classNames('navbar-placeholder', styles.navbarPlaceholder)}
          style={{
            height:
              showHeaderDisplayAd && stickyNavHeight === estimatedStickyNavOffsetPx
                ? `calc(${estimatedChromePx}px + var(--bz-header-ad-height, ${headerAdFallbackPx}px))`
                : stickyNavHeight,
          }}
        />
      ) : null}
      <div
        className={classNames(styles.desktopMenuWrapper, 'desktop-menu-wrapper', {
          [styles.desktopMenuWrapperFixed]: sticky,
        })}
        id={isActiveHeader ? 'navigation-header' : undefined}
        ref={desktopMenuWrapperRef}
      >
        {showRotatingBanner && <OptimizelyTopBanner />}
        {showHeaderDisplayAd ? (
          <AdHeaderBanner
            expandedHeaderDisplayAd={expandedHeaderDisplayAd}
            headerType="desktop"
            loadAd={shouldRenderHeaderDisplayAd}
          />
        ) : null}
        {!hideTopBar && (
          <ErrorBoundary name="navigation-top-bar">
            <TopBar items={featuredMenu} />
          </ErrorBoundary>
        )}
        <div
          className={classNames(styles.whiteBar, 'main-menu-bar', {
            [styles.whiteBarBordered]: hideQuoteBar,
          })}
        >
          <div className={classNames(styles.barLayoutWidth, 'main-menu-wrapper')}>
            <div className={`${styles.barContentLayout} bar-content-layout`}>
              <ErrorBoundary name="benzinga-logo-wrapper">
                <NavigationLogo deviceType="desktop" logoVariant={logoVariant} />
              </ErrorBoundary>
              {!hideMenuBar && (
                <>
                  <div className={`${styles.primaryMenuWrapper} primary-menu-wrapper`}>
                    <ul className={classNames(styles.menuItemContainer, 'menu-item-container')}>
                      {menus &&
                        menus.primary?.map((menu: MenuInterface, i: number) => {
                          return (
                            <li key={i}>
                              <Menu menu={menu} />
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                  {!isMoneyApp ? (
                    <>
                      <button
                        aria-label="Click to open more navbar items"
                        className="more-nav-tabs-icon-container"
                        onClick={handleToggleMenuItemsDropdown}
                      >
                        <Icon className="text-2xl text-black" icon={faEllipsisH} />
                        {<MoreMenuItemsDropdown isVisible={isMoreItemsDropdownVisible} menus={menus} />}
                      </button>
                      <div className={`${styles.secondaryMenuWrapper} secondary-menu-wrapper`}>
                        {menus &&
                          menus.secondary
                            ?.filter((menuItem: MenuInterface) => menuItem?.deviceType !== 'mobile')
                            ?.map((menu: MenuInterface, i: number) => {
                              return <Menu key={i} menu={menu} secondary={true} />;
                            })}
                      </div>
                    </>
                  ) : (
                    menus &&
                    menus.secondary &&
                    Array.isArray(menus.secondary) &&
                    menus.secondary.length > 0 && (
                      <div className={`${styles.secondaryMenuWrapper} secondary-menu-wrapper`}>
                        <TertiaryMenu menu={menus.secondary} />
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {!hideQuoteBar ? (
          <QuoteBar
            hideSearchBar={hideSearchBar}
            initialTrendingTickers={initialTrendingTickers}
            initialTrendingTopics={initialTrendingTopics}
            marketTickers={tickers}
            onInput={onSearch}
            primaryQuotesLabel={primaryQuotesLabel}
            quotes={quotes}
            recentTickers={recentTickers}
          />
        ) : null}
        <ErrorBoundary name="breaking-news-banner">
          {showEmailVerificationBanner && user ? (
            <div
              className={classNames('breaking-news-wrapper', {
                'breaking-news-wrapper--visible': emailStripVisible,
              })}
            >
              <div className="flex flex-col w-full breaking-news-banner">
                <EmailVerificationBanner email={user.email} />
              </div>
            </div>
          ) : null}
          {breakingNews && breakingNews?.length > 0 ? (
            <div
              className={classNames('breaking-news-wrapper', {
                'breaking-news-wrapper--visible': breakingNewsVisible,
              })}
            >
              <BreakingNewsBanner data={breakingNews} />
            </div>
          ) : null}
          {isMoneyApp && moneyBannerLink && moneyBannerText ? (
            <div className={`${styles.moneyBreaking} money-breaking`}>
              <a href={moneyBannerLink}>{moneyBannerText}</a>
            </div>
          ) : null}
        </ErrorBoundary>
      </div>
    </>
  );
};

export const TertiaryMenu: React.FC<{ menu: MenuInterface[] }> = ({ menu }) => {
  const [menuVisible, setMenuVisible] = React.useState(false);
  const isLoggedIn = useHydrate(useIsUserLoggedIn(), false);
  return (
    <div className={`${styles.tertiaryMenuWrap} money-menu-wrap`}>
      <div className="menu-icon-wrapper">
        <div className="money-menu-toggle" onClick={() => setMenuVisible(!menuVisible)}>
          <span>Menu</span>
          <Icon icon={menuVisible ? faXmark : faBars} />
        </div>
      </div>
      <div className={`tertiary-menu-items ${menuVisible ? 'show' : ''}`}>
        {menu &&
          menu.map((menuItem: MenuInterface, i: number) => {
            return (
              <div className="money-toggle-wrap" key={i}>
                <MenuItemLink
                  className={'parent-menu-item'}
                  data-action="Navigation Main Menu Click"
                  href={convertToRelativeUrl(
                    isLoggedIn && menuItem.logged_in_href ? isLoggedIn && menuItem.logged_in_href : menuItem.href,
                  )}
                  title={menuItem.label}
                >
                  {menuItem.label}
                </MenuItemLink>
                {menuItem.subnav ? (
                  <div className="sub-nav-items">
                    <MenuItems items={menuItem.subnav.links} />
                  </div>
                ) : null}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export const PremiumBtn: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ className, ...props }) => (
  <a className={classNames(styles.premiumBtn, className)} {...props} />
);

export const PrimaryNavButton: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement> & { color: string }> = ({
  className,
  color,
  style,
  ...props
}) => (
  <a className={classNames(styles.primaryNavButton, className)} style={{ background: color, ...style }} {...props} />
);

export const BarContentLayout: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={classNames(styles.barContentLayout, className)} {...props} />
);

export const PrimaryMenuWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={classNames(styles.primaryMenuWrapper, className)} {...props} />
);

export const SecondaryMenuWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={classNames(styles.secondaryMenuWrapper, className)} {...props} />
);
